import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import MajorInfoBox from "./MajorInfoBox";
import MinorInfoBox from "./MinorInfoBox";
import stockBall from '../icons/stock_ball.jpg';

/**
 * Componente FullInfoBox - Genérico e reutilizável
 * 
 * Props opcionais:
 * @param {Object} mainNews - Notícia principal a ser exibida na caixa maior
 * @param {Object} sideNews1 - Primeira notícia lateral 
 * @param {Object} sideNews2 - Segunda notícia lateral
 * 
 * Cada objeto de notícia deve ter:
 * @param {string} title - Título da notícia
 * @param {string} description - Descrição da notícia
 * @param {string} mediaUrl - URL da imagem
 * @param {string|number} id - ID único da notícia para navegação
 * 
 * Exemplo de uso com notícias específicas:
 * <FullInfoBox 
 *   mainNews={{ id: 1, title: "Título", description: "Descrição", mediaUrl: "url" }}
 *   sideNews1={{ id: 2, title: "Lateral 1", description: "Desc", mediaUrl: "url" }}
 *   sideNews2={{ id: 3, title: "Lateral 2", description: "Desc", mediaUrl: "url" }}
 * />
 * 
 * Exemplo de uso com defaults:
 * <FullInfoBox />
 */
export default function FullInfoBox({ 
  mainNews, 
  sideNews1, 
  sideNews2,
  headerColor = '#1976d2',
  headerText = '',
  bgFilter = 'hue-rotate(10deg)'
}) {
    const navigate = useNavigate();

    const defaultMainNews = { id: null, title: "Bem-vindo ao Arena Esporte Total", subtitle: "Adicione sua primeira notícia para começar!", mediaUrl: stockBall };
    const defaultSideNews1 = { id: null, title: "Notícias em Destaque", subtitle: "Suas notícias aparecerão aqui automaticamente", mediaUrl: stockBall };
    const defaultSideNews2 = { id: null, title: "Últimas Atualizações", subtitle: "Mantenha-se atualizado com as últimas novidades", mediaUrl: stockBall };

    const displayMainNews = mainNews || defaultMainNews;
    const displaySideNews1 = sideNews1 || defaultSideNews1;
    const displaySideNews2 = sideNews2 || defaultSideNews2;

    const handleMainNewsClick = () => {
        if (displayMainNews.id) {
            navigate(`/article/${displayMainNews.id}`);
        }
    };

    const handleSideNews1Click = () => {
        if (displaySideNews1.id) {
            navigate(`/article/${displaySideNews1.id}`);
        }
    };

    const handleSideNews2Click = () => {
        if (displaySideNews2.id) {
            navigate(`/article/${displaySideNews2.id}`);
        }
    };

    return <Box
      sx={{
        width: { xs: "100vw", sm: 520, md: "100%" },
        position: 'relative',
        borderRadius: { xs: 0, sm: 3},
        margin: {sm: 1, md: 2 },
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
        display: 'flex',
        justifyContent: {
          xs: 'flex-start',
          md: 'center',
        },
        alignItems: {
          xs: 'flex-start',
          md: 'start',
        },
        overflow: 'hidden',
        flexWrap: 'wrap'
      }}>
      {/* Linha de topo com box colorida e texto */}
      {(headerText || headerColor) && (
        <Box sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pt: 2,
          pb: 1,
        }}>
          <Box sx={{
            width: 8,
            height: 26,
            borderRadius: 1,
            backgroundColor: headerColor,
            flexShrink: 0,
          }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: 18, color: headerColor }}>
            {headerText}
          </Typography>
        </Box>
      )}
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            zIndex: -1,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* Imagem de fundo */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              backgroundImage: `url(${stockBall})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.2,
              filter: bgFilter,
            }}
          />
          {/* Gradiente para fade-out na parte de baixo */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              background: 'linear-gradient(to bottom, rgba(255,255,255,0) 60%, #fff 100%)',
              pointerEvents: 'none',
            }}
          />
        </Box>
        
        <MajorInfoBox 
          subtitle={displayMainNews.subtitle} 
          title={displayMainNews.title}
          mediaUrl={displayMainNews.mediaUrl}
          driveFileId={displayMainNews.driveFileId}
          thumbnailUrl={displayMainNews.thumbnailUrl}
          onClick={handleMainNewsClick}
          isClickable={!!displayMainNews.id}
        />
        <Box
          sx={{
            flexDirection: {
              xs: "column",
              sm: "row",
              md: "column"
            },
            display: "flex",
            justifyContent:"center",
            alignItems: {
              xs: "center",
              sm: "flex-start",
            },
            transform:{ xs: "translateX(-15%)",
                      sm: "translate(0)" },
            marginY: { xs: 1, md: 2 },
            width: { xs: 500, md: 'auto' },
            gap: { xs: 1, md: 0 },
            overflow: 'hidden'
          }}>
          <MinorInfoBox 
            subtitle={displaySideNews1.subtitle} 
            title={displaySideNews1.title}
            mediaUrl={displaySideNews1.mediaUrl}
            driveFileId={displaySideNews1.driveFileId}
            thumbnailUrl={displaySideNews1.thumbnailUrl}
            onClick={handleSideNews1Click}
            isClickable={!!displaySideNews1.id}
          />
          <MinorInfoBox 
            subtitle={displaySideNews2.subtitle} 
            title={displaySideNews2.title}
            mediaUrl={displaySideNews2.mediaUrl}
            driveFileId={displaySideNews2.driveFileId}
            thumbnailUrl={displaySideNews2.thumbnailUrl}
            onClick={handleSideNews2Click}
            isClickable={!!displaySideNews2.id}
          />
        </Box>
        </Box>
}